import React from 'react';
import Icon from '../AppIcon';
import useTranslation from '../../hooks/useTranslation';
import useEscapeKey from '../../hooks/useEscapeKey';

/**
 * ConfirmDialog — canonical shared confirmation dialog.
 *
 * Props:
 *   isOpen          boolean          — controls visibility
 *   title           string           — header title
 *   message         string|node      — body message
 *   warning         string|null      — optional red warning block below message
 *   icon            string|null      — lucide icon name shown in header icon circle
 *   confirmVariant  'primary'|'danger'  — button colour (default 'primary')
 *   confirmLabel    string           — confirm button text (default : t('common.confirm'))
 *   cancelLabel     string           — cancel button text (default : t('common.cancel'))
 *   onConfirm       fn               — called on confirm click
 *   onCancel        fn               — called on cancel / backdrop click
 *   confirmOnly     boolean          — hides cancel; confirm button calls onCancel (info-only mode)
 *   loading         boolean          — disables buttons + shows spinner on confirm
 *   children        node             — optional extra body content below message
 */
const ConfirmDialog = ({
  isOpen,
  title,
  message,
  warning = null,
  icon = null,
  confirmVariant = 'primary',
  confirmLabel = null,
  cancelLabel = null,
  onConfirm,
  onCancel,
  confirmOnly = false,
  loading = false,
  children,
}) => {
  const { t } = useTranslation();
  // Echap = Annuler (sauf pendant l'operation, comme le clic sur le fond).
  useEscapeKey(onCancel, !!isOpen && !loading);
  if (!isOpen) return null;

  const isDanger = confirmVariant === 'danger';

  const iconName = icon || (isDanger ? 'Trash2' : 'AlertCircle');
  const iconBg = isDanger ? 'bg-red-50' : 'bg-orange-50';
  const iconColor = isDanger ? 'text-red-500' : 'text-primary';

  const confirmBtnClass = isDanger
    ? 'bg-red-600 hover:bg-red-700 text-white' :'bg-primary hover:bg-primary-dark text-white';

  const handleConfirmClick = confirmOnly ? onCancel : onConfirm;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 backdrop-blur-sm">
      {/* Backdrop click */}
      <div className="absolute inset-0" onClick={!loading ? onCancel : undefined} />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100">
          <div className={`flex-shrink-0 w-9 h-9 rounded-full ${iconBg} flex items-center justify-center`}>
            <Icon name={iconName} size={18} className={iconColor} />
          </div>
          <h3 className="text-sm font-semibold text-gray-900 leading-snug">{title}</h3>
        </div>

        {/* Body */}
        <div className="px-6 py-4">
          {message && (
            <p className="text-sm text-gray-600">{message}</p>
          )}
          {warning && (
            <p className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2 mt-3">{warning}</p>
          )}
          {children && (
            <div className="mt-3">{children}</div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          {!confirmOnly && (
            <button
              onClick={onCancel}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {cancelLabel ?? t('common.cancel')}
            </button>
          )}
          <button
            onClick={handleConfirmClick}
            disabled={loading}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 ${confirmBtnClass}`}
          >
            {loading && <Icon name="Loader2" size={13} className="animate-spin" />}
            {confirmLabel ?? t('common.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
